// redux/thunks/assistantManagement.thunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { axiosAssistant, axiosAssistantPublic } from "../../../api/axios";
import { ASSISTANTS_PATHS } from "../../../api/endpoints";

const errorPayload = (error, fallbackMessage) => ({
  status: error?.response?.status || null,
  message:
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage,
  data: error?.response?.data || null,
});

// ─────────────────────────────────────────────────────────────
// CRUD assistants (authentifié — médecin connecté)
// ─────────────────────────────────────────────────────────────

export const createAssistant = createAsyncThunk(
  "assistantManagement/createAssistant",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosAssistant.post(ASSISTANTS_PATHS.CREATE, payload);
      return res?.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, "Failed to create assistant"));
    }
  }
);

export const fetchAssistants = createAsyncThunk(
  "assistantManagement/fetchAssistants",
  async ({ status, search } = {}, { rejectWithValue }) => {
    try {
      const res = await axiosAssistant.get(ASSISTANTS_PATHS.LIST, {
        params: { status, search },
      });
      return res?.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, "Failed to fetch assistants"));
    }
  }
);

export const fetchAssistantById = createAsyncThunk(
  "assistantManagement/fetchAssistantById",
  async (assistantId, { rejectWithValue }) => {
    try {
      const res = await axiosAssistant.get(ASSISTANTS_PATHS.BY_ID(assistantId));
      return res?.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, "Failed to fetch assistant"));
    }
  }
);

export const updateAssistant = createAsyncThunk(
  "assistantManagement/updateAssistant",
  async ({ assistantId, payload }, { rejectWithValue }) => {
    try {
      // Backend expose PATCH, pas PUT
      const res = await axiosAssistant.patch(ASSISTANTS_PATHS.UPDATE(assistantId), payload);
      return res?.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, "Failed to update assistant"));
    }
  }
);

export const resendAssistantInvitation = createAsyncThunk(
  "assistantManagement/resendInvitation",
  async (assistantId, { rejectWithValue }) => {
    try {
      const res = await axiosAssistant.post(ASSISTANTS_PATHS.RESEND_INVITATION(assistantId));
      return res?.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, "Failed to resend invitation"));
    }
  }
);

export const deleteAssistant = createAsyncThunk(
  "assistantManagement/deleteAssistant",
  async (assistantId, { rejectWithValue }) => {
    try {
      const doctorToken =
        Cookies.get("doctorAccessToken") ||
        Cookies.get("doctorToken") ||
        localStorage.getItem("doctorAccessToken") ||
        localStorage.getItem("doctorToken");
      const response = await axiosAssistant.delete(
        ASSISTANTS_PATHS.DELETE(assistantId),
        {
          headers: doctorToken
            ? { Authorization: `Bearer ${doctorToken}` }
            : undefined,
        }
      );
      return {
        assistantId: response?.data?.assistantId || assistantId,
        ...response?.data,
      };
    } catch (error) {
      return rejectWithValue(errorPayload(error, "Failed to delete assistant"));
    }
  }
);

// ─────────────────────────────────────────────────────────────
// Activation par lien d'invitation (PUBLIC — pas de session active)
// GET  /assistants/activation/verify?token=...
// POST /assistants/activation/set-password
// ─────────────────────────────────────────────────────────────

export const verifyAssistantActivationToken = createAsyncThunk(
  "assistantManagement/verifyActivationToken",
  async (token, { rejectWithValue }) => {
    try {
      const res = await axiosAssistantPublic.get(ASSISTANTS_PATHS.ACTIVATION_VERIFY, {
        params: { token },
      });
      return res?.data;
    } catch (error) {
      return rejectWithValue(
        errorPayload(error, "Failed to verify assistant activation token")
      );
    }
  }
);

// ─────────────────────────────────────────────────────────────
// CORRIGÉ : on stocke le token assistant renvoyé par le backend
// après activation, exactement comme le fait loginAssistant.
// Sans ça, l'assistant arrive sur AssistantAvailabilityPage sans
// session -> la route protégée le renvoie vers /login (faux "logout").
// ─────────────────────────────────────────────────────────────

export const setAssistantActivationPassword = createAsyncThunk(
  "assistantManagement/setActivationPassword",
  async ({ token, password, confirmPassword }, { rejectWithValue }) => {
    try {
      const res = await axiosAssistantPublic.post(
        ASSISTANTS_PATHS.ACTIVATION_SET_PASSWORD,
        { token, password, confirmPassword }
      );
      const data = res?.data;

      const accessToken = data?.accessToken || data?.token || null;
      const assistantId = data?.assistant?.id || data?.assistantId || null;
      const doctorId = data?.assistant?.doctorId || data?.doctorId || null;
      const assistantName = data?.assistant?.fullName || data?.assistant?.name || null;
      const assistantEmail = data?.assistant?.email || data?.email || null;

      if (accessToken) {
        Cookies.set("assistantAccessToken", accessToken);
        localStorage.setItem("assistantAccessToken", accessToken);
        localStorage.setItem("userRole", "assistant");
      }
      if (assistantId) localStorage.setItem("assistantId", String(assistantId));
      if (doctorId) localStorage.setItem("assistantDoctorId", String(doctorId));
      if (assistantName) localStorage.setItem("assistantName", String(assistantName));
      if (assistantEmail) localStorage.setItem("assistantEmail", String(assistantEmail));

      return { ...data, accessToken, assistantId, doctorId, assistantName, assistantEmail };
    } catch (error) {
      return rejectWithValue(errorPayload(error, "Failed to set assistant password"));
    }
  }
);

// ─────────────────────────────────────────────────────────────
// Auth assistant — POST /assistants/auth/login
// ─────────────────────────────────────────────────────────────

export const loginAssistant = createAsyncThunk(
  "assistantManagement/loginAssistant",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const res = await axiosAssistantPublic.post(ASSISTANTS_PATHS.AUTH_LOGIN, {
        email,
        password,
      });
      const data = res?.data;

      const token = data?.accessToken || data?.token || null;
      const assistantId = data?.assistant?.id || data?.assistantId || null;
      const doctorId = data?.assistant?.doctorId || data?.doctorId || null;
      const assistantName = data?.assistant?.fullName || data?.assistant?.name || null;
      const assistantEmail = data?.assistant?.email || data?.email || null;

      if (token) {
        Cookies.set("assistantAccessToken", token);
        localStorage.setItem("assistantAccessToken", token);
        localStorage.setItem("userRole", "assistant");
      }
      if (assistantId) localStorage.setItem("assistantId", String(assistantId));
      if (doctorId) localStorage.setItem("assistantDoctorId", String(doctorId));
      if (assistantName) localStorage.setItem("assistantName", String(assistantName));
      if (assistantEmail) localStorage.setItem("assistantEmail", String(assistantEmail));

      return { ...data, accessToken: token, assistantId, doctorId, assistantName, assistantEmail };
    } catch (error) {
      return rejectWithValue(errorPayload(error, "Failed to login assistant"));
    }
  }
);

// ─────────────────────────────────────────────────────────────
// Rendez-vous du médecin, vus par l'assistant connecté
// ─────────────────────────────────────────────────────────────

export const fetchDoctorRendezvousAsAssistant = createAsyncThunk(
  "assistantManagement/fetchDoctorRendezvousAsAssistant",
  async (doctorId, { rejectWithValue }) => {
    try {
      const res = await axiosAssistant.get(
        ASSISTANTS_PATHS.DOCTOR_RDV_AS_ASSISTANT(doctorId)
      );
      return res?.data;
    } catch (error) {
      return rejectWithValue(
        errorPayload(error, "Failed to fetch doctor rendez-vous as assistant")
      );
    }
  }
);